import PerformanceLevel from "./PerformanceLevel";

class StageManager {
    /**
     * 
     * @param {Object} stages Object with id:instance pairs
     * @param {Number} framerate
     * @param {Number} performanceLevel - see PerformanceLevel
     * @param {String} timingMode - see createjs.Ticker.timingMode
     * @returns {undefined}
     */
    constructor(stages, framerate, performanceLevel, timingMode = createjs.Ticker.RAF_SYNCHED) {
        this.verbosity = 1;
        this.setPerformanceLevel(performanceLevel);
        this._stages = stages;
        this._framerate = Math.min(MAX, framerate);
        this._deepfrozen = false;
        this._mouseOverSuspended = false;
        if (this.verbosity > 0)
            console.log("StageManager.constructor enabled:" + this._enabled + ", perf.level:" + performanceLevel + ", fps:" + this._framerate);
        //We create some extra props on each stage:
        // - renderOnUpdate, boolean indicating if the stage should be rendered
        // - renderOnce, boolean indicating if the stage should render once at next update
        // - framerate, stage specific framerate, used for temporary framerate changes
        // - framerateDefault, stage default framerate, used for temporary framerate changes
        // - ticksSkip, int indicating how may ticks should be skipped based on framerate and Ticker fps
        // - tickNext, int indicates at which Ticker tick should the stage be rendered next
        var stages = [];
        for (var id in this._stages) {
            var stage = this._stages[id];
            stage.snapToPixelEnabled = false;
            stage.renderOnUpdate = true;
            stage.renderOnce = false;
            this.setStageFramerate(id, this._framerate, true);
            stages.push(stage);
        }
        //find topmost stage for mouseover stuff (see enableStageMouseOver)
        var top = stages[0], prev = top, n = stages.length;
        for (var j = 0; j < n; j++) {
            for (var i = 0; i < n; i++) {
                stage = stages[i];
                if (stage.nextStage == top)
                    top = stage;
            }
            if (prev == top)
                break;
        }
        this._topStage = top;
        //Default all mouseovers are enabled (depending on DEFAULT_MOUSEOVER_FREQUENCY!):
        this.enableAllStagesMouseOver(StageManager.DEFAULT_MOUSEOVER_FREQUENCY);
        //createjs.Ticker.setFPS deprecated per createjs-1.0.0, 
        //  kept for backwards compatibility
        if (createjs.Ticker.hasOwnProperty("framerate")) createjs.Ticker.framerate = MAX;
        else createjs.Ticker.setFPS(MAX);
        createjs.Ticker.timingMode = timingMode;
        createjs.Ticker.addEventListener("tick", this._handleTick.bind(this));
    }

    setPerformanceLevel(value) {
        this._performanceLevel = value;
        this._enabled = value < PerformanceLevel.HIGH;
    }

    getPerformanceLevel() {
        return this._performanceLevel;
    }

    _handleTick(e) {
        this._update();
    }

    /**
     * Implements frameskipping for certain stages to enforce lower framerate
     */
    _update(forceAll) {
        var t = createjs.Ticker.getTicks();
        for (var id in this._stages) {
            var stage = this._stages[id];
            if (t >= stage.tickNext) {
                if (forceAll || !this._enabled || stage.renderOnUpdate || stage.renderOnce)
                    stage.update();
                stage.renderOnce = false;
                stage.tickNext = t + stage.ticksSkip;
            }
        }
    }

    getStages() {
        //this._stages is Object with stage id as keys
        return this._stages;
    }

    /**
     * Set framerate of stage.
     * @param {type} stageId
     * @param {type} framerate Should be divisor of 120 {1,2,3,4,5,6,8,10,12,15,20,24,30,40,60,120}, or -1 for stage default
     * @param {type} asDefault Boolean indicating if this is going to be the default framerate for this stage from now on
     * @returns {undefined}
     */
    setStageFramerate(stageId, framerate, asDefault) {
        if (framerate > 0 && Math.abs(Math.round(MAX / framerate) - MAX / framerate) > .0000001) {
            console.warn("StageManager.setStageFramerate, ignored. Not a divisor of " + MAX + "! : " + framerate + " (" + stageId + ")");
            return;
        }
        var stage = this._stages[stageId];
        if (!stage)
            return;
        if (framerate == -1)
            framerate = stage.framerateDefault;
        stage.framerate = framerate;//purely for debugging, not used
        if (asDefault)
            stage.framerateDefault = framerate;
        //
        var t = createjs.Ticker.getTicks();
        stage.ticksSkip = MAX / framerate;
        stage.tickNext = t + stage.ticksSkip;
        if (this.verbosity > 1)
            console.log("StageManager.setStageFramerate: " + stageId + " " + framerate + " " + stage.ticksSkip + " " + stage.tickNext);
    }

    setAllStagesFramerate(framerate, asDefault) {
        if (!this._enabled || this._deepfrozen)
            return;
        if (this.verbosity > 1)
            console.log("StageManager.setAllStagesFramerate " + framerate, asDefault);
        for (var id in this._stages) {
            this.setStageFramerate(id, framerate, asDefault);
        }
    }

    setStageActivityLevel(stageId, level) {
        if (!this._enabled || this._deepfrozen)
            return;
        if (this.verbosity > 1)
            console.log("StageManager.setStageActivityLevel " + stageId + " " + level);
        if (level == null)
            level = StageManager.ALL;
        var stage = this._stages[stageId];
        if (!stage)
            return;
        stage.renderOnUpdate = (level & StageManager.RENDER) == StageManager.RENDER;
        stage.tickOnUpdate = (level & StageManager.TICK) == StageManager.TICK;
        stage.renderOnce = true;
        if (this._stateBeforeFreeze)
            delete this._stateBeforeFreeze[stageId];
    }

    setAllStagesActivityLevel(level) {
        if (!this._enabled || this._deepfrozen)
            return;
        if (this.verbosity > 1)
            console.log("StageManager.setAllStagesActivityLevel " + level);
        for (var id in this._stages) {
            this.setStageActivityLevel(id, level);
        }
    }

    //method to flag stage for 1 update
    setStageRenderOnce(stageId, bool) {
        if (!this._enabled || this._deepfrozen)
            return;
        if (this.verbosity > 1)
            console.log("StageManager.setStageRenderOnce " + stageId + " " + bool);
        var stage = this._stages[stageId];
        if (!stage)
            return;
        stage.renderOnce = bool;
        stage.tickNext = createjs.Ticker.getTicks();//immediately!
    }

    setAllStagesRenderOnce(bool) {
        if (!this._enabled || this._deepfrozen)
            return;
        if (this.verbosity > 1)
            console.log("StageManager.setAllStagesRenderOnce " + bool);
        for (var id in this._stages) {
            this.setStageRenderOnce(id, bool);
        }
    }

    /**
     * Enable mouseovers on specified stage.
     * NB: when we call a stage.enableMouseOver(0) on the top stage, 
     *	mouseovers are disabled on the other stages as well.
     *	See:http://www.createjs.com/docs/easeljs/classes/Stage.html#property_nextStage
     *		1. The top-most (first) stage must have mouse over interactions enabled (via enableMouseOver)
     *		3. All relay targets will share the frequency value of the top-most stage
     *	So it might even be good practice to have an empty top stage, so it has no children to be checked.
     * @param {String} stageId
     * @param {int} frequency. Leave empty/use null for default frequency. Use 0 to disable mouseovers.
     * @returns {undefined}
     */
    enableStageMouseOver(stageId, frequency) {
        if (this.verbosity > 1)
            console.log("StageManager.enableStageMouseOver " + stageId + " " + frequency);
        var stage = this._stages[stageId];
        if (!stage)
            return;
        if (frequency == null)
            frequency = StageManager.DEFAULT_MOUSEOVER_FREQUENCY;
        //
        var suspended = this._mouseOverSuspended;
        //All stages have same frequency by design 
        //	See:http://www.createjs.com/docs/easeljs/classes/Stage.html#property_nextStage
        //So we use the highest current frequency for this stage as well
        var frequencyMax = frequency;
        if (suspended) {
            frequencyMax = 0;
        }
        else {
            for (var id in this._stages) {
                frequencyMax = Math.max(frequencyMax, this._stages[id].mouseOverFrequency)
            }
        }
        stage.enableMouseOver(frequency > 0 ? frequencyMax : 0);
        stage.mouseOverFrequency = frequency;//remember requested frequency, not max!
        //
        if (stage != this._topStage) {
            this._topStage.enableMouseOver(frequencyMax);
        }
    }

    enableAllStagesMouseOver(frequency) {
        if (this.verbosity > 1)
            console.log("StageManager.enableStageMouseOver " + stageId + " " + frequency);
        for (var id in this._stages) {
            this.enableStageMouseOver(id, frequency);
        }
    }

    /**
     * Temporarily disable mouseovers
     * @param {Boolean} suspend, whether to suspend mouseovers or unsuspend
     */
    suspendMouseOver(suspend) {
        this._mouseOverSuspended = suspend;
        //reapply all mouse over frequencies, with new _mouseOverSuspended flag
        for (var id in this._stages) {
            this.enableStageMouseOver(id, this._stages[id].mouseOverFrequency);
        }
    }

    /**
     * Temporarily freeze all stage except the specified stages.
     * Call unfreeze to set to previous state
     * NB:
     * Any setStageActivityLevel calls etc will still be executed, so stages can be unfrozen before the unfreeze call
     *	unless argument deepFreeze is true!
     * @param Array exceptStageIds, optional
     * @param Boolean deepFreeze, optional. Ignore all other calls until unfreeze.
     * @returns {undefined}
     */
    freeze(exceptStageIds, deepFreeze) {
        if (this.verbosity > 1)
            console.log("StageManager.freeze " + (exceptStageIds ? exceptStageIds.join(",") : "") + " " + this._deepfrozen + " " + deepFreeze);
        //ignore normal freeze if we are currently deepfrozen
        if (this._deepfrozen && !deepFreeze)
            return;
        if (!this._stateBeforeFreeze)
            this._stateBeforeFreeze = {};
        for (var id in this._stages) {
            if (!exceptStageIds || exceptStageIds.indexOf(id) == -1) {
                //only freeze stages that have not been frozen in earlier calls to avoid overwriting _stateBeforeFreeze!
                if (!this._stateBeforeFreeze[id]) {
                    var stage = this._stages[id];
                    //this.setStageRenderOnce(id, true);
                    this._stateBeforeFreeze[id] = { renderOnUpdate: stage.renderOnUpdate, tickOnUpdate: stage.tickOnUpdate };
                    stage.renderOnUpdate = false;
                    stage.tickOnUpdate = false;
                }
            }
        }
        this._deepfrozen = deepFreeze;
    }

    unfreeze(deepFreeze) {
        if (this.verbosity > 1)
            console.log("StageManager.unfreeze " + (this._stateBeforeFreeze != null) + " " + this._deepfrozen + " " + deepFreeze);
        //ignore normal unfreeze if we are currently deepfrozen
        if (!this._stateBeforeFreeze || (this._deepfrozen && !deepFreeze))
            return;
        for (var id in this._stateBeforeFreeze) {
            var state = this._stateBeforeFreeze[id];
            var stage = this._stages[id];
            stage.renderOnUpdate = state.renderOnUpdate;
            stage.tickOnUpdate = state.tickOnUpdate;
        }
        this._stateBeforeFreeze = null;
        this._deepfrozen = false;
    }

    report() {
        var lines = [];
        lines.push("StageManager.report, enabled: " + this._enabled);
        lines.push("  PerformanceLevel: " + this._performanceLevel);
        for (var id in this._stages) {
            var stage = this._stages[id];
            id = (id + "                 ").substr(0, 12);
            lines.push("  " + id + ": " + (stage.renderOnUpdate ? "render " : "       ") + (stage.tickOnUpdate ? "tick " : "     ") + stage.framerate + "  " + stage.mouseOverFrequency);
        }
        var str = lines.join("\n");
        console.log(str);
    }
}
//enum for activity levels
StageManager.NONE = 0;
StageManager.RENDER = 1;
StageManager.TICK = 2;
StageManager.ALL = StageManager.RENDER | StageManager.TICK;

StageManager.FREEZE_NONE = 0;
StageManager.FREEZE_NORMAL = 1;
StageManager.FREEZE_DEEP = 2;

//constants
var MAX = 60;
StageManager.DEFAULT_MOUSEOVER_FREQUENCY = 15;

export default StageManager;
